<template>
  <div>
    <b-row class="mb-3">
      <b-col lg="7">
        <h4 class="mb-4">{{ title }}</h4>
      </b-col>

      <b-col cols="12" class="locked-customer-lg mb-3">
        <LockedCustomer />
      </b-col>
      <b-col class="pt-lg-5 pt-xl-0" cols="12" lg="6">
        <p>
          Use this tool to create many port in orders at once from a
          <a
            href="https://support.microsoft.com/en-us/office/save-a-workbook-to-text-format-txt-or-csv-3e9a9d6c-70da-4255-aa28-fcacf1f081e6"
            target="_blank"
            >CSV file</a
          >. <br />Reference for columns and data can be downloaded from
          Advanced > Download validation file
        </p>
        <R-Label :theme="labelTheme" value="Select CSV file:" />
        <div @click="clearFile">
          <b-form-file
            class="input_style mb-3"
            v-model="file"
            ref="file-input-porting"
            accept=".csv"
            placeholder="Choose a file or drop it here..."
            drop-placeholder="Drop file here..."
            @change="onFileChange"
          ></b-form-file>
        </div>
        <a
          class="adv-btn"
          :href="'#collapse-porting'"
          @click.prevent
          v-b-toggle.collapse-porting
        >
          <h6 class="mb-0">
            Advanced
            <ArrowDownIcon class="d-inline" :theme="iconTheme"></ArrowDownIcon>
          </h6>
        </a>
        <b-collapse id="collapse-porting" class="mt-2">
          <R-Button
            :secondary="true"
            :hasShadow="false"
            :size="'small'"
            @onClick="downloadValidation()"
            >Download validation file</R-Button
          >
        </b-collapse>
      </b-col>
    </b-row>

    <b-row class="">
      <b-col cols="12" class="">
        <h4 class="table-controls-bg p-3 mb-0">Porting orders</h4>
      </b-col>
      <b-col cols="12">
        <table-view
          v-model="csvData"
          :hasValidation="true"
          :validationData="csvValidationData"
          :showLoader="showLoader"
          @submit="onSubmit"
          @download="onDownload"
        >
        </table-view>
      </b-col>
    </b-row>

    <b-row v-if="getPortOrdersResults.success" v-scrollTo>
      <b-col cols="12" class="mt-3">
        <div>
          <h4 class="table-controls-bg p-3 mb-0">
            Results
            <R-Button
              v-if="getPortOrdersResults.data.orders.length > 0"
              class="float-right"
              :size="'small'"
              @onClick="downloadPortingResults"
              >Download</R-Button
            >
          </h4>
        </div>
        <div class="table-controls">
          <p class="h6 text-left mb-2">
            Success: {{ getPortOrdersResults.success }}
          </p>
          <p class="h6 text-left mb-2">
            Status: {{ getPortOrdersResults.data.status }}
          </p>
          <p class="h6 text-left mb-2">
            Message: {{ getPortOrdersResults.message }}
          </p>
          <b-table
            hover
            :head-variant="'light'"
            :items="getPortOrdersResults.data.orders"
            :busy="isBusy"
            :fields="tableFields"
          >
            <template v-slot:cell(phoneNumbers)="data">
              {{ formattedPhonenumbers(data.item.phoneNumbers) }}
            </template>

            <template v-slot:table-busy>
              <b-spinner
                class="spinner-lg mt-3"
                variant="primary"
                label="Loading"
              ></b-spinner>
              <R-Label
                className="justify-content-center mt-2"
                :theme="labelSpinner"
                :value="'Loading...'"
              />
            </template>

            <template v-slot:empty="scope">
              <h4 class="p-3">{{ scope.emptyText }}</h4>
            </template>
            <template v-slot:emptyfiltered="scope">
              <h4 class="p-3">{{ scope.emptyFilteredText }}</h4>
            </template>
          </b-table>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import LockedCustomer from "@/vue/components/LockedCustomer.vue";

var Tabulator = require("tabulator-tables");
import Papa from "papaparse";
import TableView from "@/vue/components/tabulator/Index.vue";

import { notify, DURATION, TYPE } from "@8x8/oxygen-toaster";

import { createValidationData, padPhoneNumber } from "@/vue/utils/helper";
import moment from "moment";

export default {
  name: "port-orders",
  props: ["title"],
  components: {
    TableView,
    LockedCustomer,
  },
  data() {
    return {
      labelTheme: { fontWeight: 600 },
      iconTheme: { color: "#0376DA", size: "18px" },
      labelSpinner: { fontColor: "#0376da", fontWeight: 600 },
      showLoader: false,
      file: [],

      csvData: [],
      csvValidationData: [],

      emptyText: "There are no records to show",
      emptyFilteredText: "No Search Results",
      isBusy: false,
      tableFields: [
        { key: "uuid", label: "Order#", class: "text-center" },
        { key: "status", class: "text-center" },
        {
          key: "failureReason",
          class: "w-auto",
          formatter: "formattedCellData",
        },
        {
          key: "reasonComments",
          class: "w-auto",
          formatter: "formattedCellData",
        },
        { key: "errorLabel", class: "w-auto", formatter: "formattedCellData" },
        {
          key: "phoneNumbers",
          class: "w-auto",
          formatter: "formattedCellData",
        },
        {
          key: "targetTemporaryNumbers",
          class: "w-auto",
          label: "Temporary Numbers",
          formatter: "formattedCellData",
        },
        {
          key: "customerMessage",
          class: "w-auto",
          formatter: "formattedCellData",
        },
      ],
    };
  },
  directives: {
    scrollTo: {
      // scroll element into view
      inserted: function (el) {
        el.scrollIntoView({ behavior: "smooth" });
      },
      update: function (el) {
        el.scrollIntoView({ behavior: "smooth" });
      },
    },
  },
  computed: {
    ...mapGetters([
      "getCustomer",
      "getPortOrdersResults",
      "getDids",
      "getDmsCountries",
    ]),
  },
  watch: {
    getDmsCountries(countries) {
      this.csvValidationData.Phone_Country.push({
        type: "in",
        parameters: countries.join("|"),
      });
    },
  },
  mounted() {
    if (!this.getCustomer.customerName) {
      this.showModal();
    }

    this.fetchDmsCountries();

    Papa.parse("/csv/validation/porting_data_validation.csv", {
      download: true,
      skipEmptyLines: true,
      header: true,
      complete: (result, file) => {
        console.log("papaparse validation file");
        console.log(result, file);
        this.csvValidationData = createValidationData(result.data);
        this.$nextTick(() => {
          this.csvData = this.tableColumnsFromValidation(result.data);
        });
      },
    });
  },
  methods: {
    ...mapActions(["sendPortOrders"]),
    formattedCellData(value) {
      return value || "N/A";
    },
    formattedPhonenumbers(data) {
      return data.join(",");
    },
    showModal() {
      this.$root.$emit("bv::show::modal", "modal-customer-lock");
    },
    downloadValidation() {
      saveAs(
        "/csv/validation/porting_data_validation.csv",
        "BulkPortInValidation.csv"
      );
    },
    downloadPortingResults() {
      let csv = Papa.unparse(
        this._.map(this.getPortOrdersResults.data.orders, (item) =>
          this._.pick(item, [
            "uuid",
            "status",
            "failureReason",
            "reasonComments",
            "errorLabel",
            "phoneNumbers",
            "customerMessage",
          ])
        )
      );
      let blob = new Blob([csv], { type: "application/csv" });
      saveAs(blob, "BulkPortInResults.csv");
    },
    // file input
    clearFile() {
      console.log("clear");
      this.$refs["file-input-porting"].reset();
    },
    onFileChange: function (e) {
      console.log("onFileChange");
      if (e.target.files[0]) {
        Papa.parse(e.target.files[0], {
          skipEmptyLines: true,
          header: true,
          complete: (result) => {
            console.log("papa parse");
            console.log(result.data);
            //create table data
            this.csvData = result.data;
          },
        });
      }
    },
    onSubmit(data) {
      console.log("onSubmit(data)");
      console.log(data);

      if (!this.getCustomer.customerName) {
        notify({
          content: "Select customer before submitting data...",
          duration: 5000,
        });
        this.showModal();
      } else {
        let orders = this.formatOrders(data);
        const tapUser = this.$store.getters["getUser"].email;
        console.log("formatted orders", orders);
        this.showLoader = true;
        // submit porting orders
        this.sendPortOrders({
          data: {
            tapUser,
            orders,
          },
          success: (response) => {
            console.log("got sendPortOrders");
            console.log(response);
            this.showLoader = false;
            notify({ content: response, duration: 5000 });
          },
          error: (response) => {
            console.log("got error sendPortOrders");
            console.log(response);
            this.showLoader = false;
            notify({
              content: response,
              type: TYPE.error,
              duration: DURATION.INFINITE,
            });
          },
        });
      }
    },
    onDownload(data) {
      console.log("onDownload(data)");
      console.log(data);
    },
    mapTableToOrderKeys(order) {
      let newPortOrder = {};
      newPortOrder.manual = true;
      newPortOrder.portInType = order.PortIn_Type;
      newPortOrder.needTemporaryNumber = true; //(order.Need_Temporary_Number == 'yes') ? true : false;
      newPortOrder.serviceAddress = {
        // "streetName": "San Fernando",
        // "streetNameSuffix": "STR",
        // "streetNumber": "88",
        // "streetNumberSuffix": "ABC",
        // "unit": "1304",
        // "city": "San Jose",
        // "state": "CA",
        // "county": "Santa Clara",
        country: order.Phone_Country,
        // "preDirectional": "NW",
        // "postDirectional": "SE",
        // "secondaryLocation": "APT",
        // "postal": "95113",
        // "zip4": "1357",
        freeForm: order.Address,
      };
      newPortOrder.authorizedPersonFirstName =
        order.PortIn_Authorized_Person_First_Name;
      newPortOrder.authorizedPersonLastName =
        order.PortIn_Authorized_Person_Last_Name;
      newPortOrder.carrier = order.Carrier;
      newPortOrder.phoneCountry = order.Phone_Country;
      newPortOrder.companyName =
        order.Company_Name || this.getCustomer.customerName;
      newPortOrder.phoneNumber = padPhoneNumber(order.Phone_Number);
      newPortOrder.targetTemporaryNumber = padPhoneNumber(
        order.Target_Temporary_Number
      );
      newPortOrder.batchName = order.Batch_Name;
      newPortOrder.billingPhoneNumber = padPhoneNumber(
        order.Billing_Phone_Number
      );
      newPortOrder.pin = order.Pin;
      newPortOrder.wirelessAccountNumber = order.Wireless_Account_Number;
      newPortOrder.providerOrderId = order.PON;
      if (order.Email_Updates) {
        newPortOrder.distributionList = {
          content: order.Email_Updates.split(",").map((email) => email.trim()),
        };
        newPortOrder.emailsEnabled = true;
      }
      const requestDate = order.Customer_Request_Date;
      if (requestDate) {
        newPortOrder.requestDateTime = this.formatDate(requestDate);
      }

      const focDate = order.FOC_Date;
      if (focDate) {
        newPortOrder.estimatedCompletionDateTime = this.formatDate(focDate);
      }

      Object.keys(newPortOrder).forEach((k) => {
        // treat empty strings as null, remove those keys
        if (newPortOrder[k] === "" || newPortOrder[k] === undefined) {
          delete newPortOrder[k];
        }
        if (typeof newPortOrder[k] === "string") {
          newPortOrder[k].trim();
        }
      });

      return newPortOrder;
    },
    formatDate(date) {
      return moment.utc(date, "M/D/YYYY").format("YYYY-MM-DDT00:00:00");
    },
    formatOrders(data) {
      const mapped = data.map(this.mapTableToOrderKeys.bind(this));
      const batches = this._.groupBy(mapped, "batchName");
      const orders = this._.map(batches, (ordersInBatch) => {
        const firstOrder = ordersInBatch[0];
        firstOrder.phoneNumbers = ordersInBatch.map((o) => o.phoneNumber);
        firstOrder.targetTemporaryNumbers = ordersInBatch.reduce(
          (carry, item) => {
            if (item.targetTemporaryNumber) {
              carry[item.phoneNumber] = item.targetTemporaryNumber;
            }
            return carry;
          },
          {}
        );
        // no longer needed
        delete firstOrder.phoneNumber;
        delete firstOrder.targetTemporaryNumber;

        return firstOrder;
      });
      return orders;
    },
    fetchAvailableDids() {
      return this.$store.dispatch("fetchDids");
    },
    fetchDmsCountries() {
      return this.$store.dispatch("fetchDmsCountries");
    },
    tableColumnsFromValidation(validationData) {
      const first = JSON.parse(JSON.stringify(validationData[0]));
      Object.keys(first).forEach((key) => (first[key] = null));
      return [first];
    },
  },
};
</script>
